import { useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardCheck } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { untimedExams } from './examPlacement';

import { EXAM_TYPE_LABEL } from '@/features/teacher/exams/constants';
import { formatDate, formatTimeRange } from '@/lib/format';
import type { FamilyExamPaper, StudentTimetableDay } from '@/types/student';

interface WeekExamsProps {
  /** The week's columns as the server sent them, each carrying its papers. */
  days: StudentTimetableDay[] | undefined;
}

/**
 * The papers sat in the week on screen, for a week with no lesson grid.
 *
 * One panel for the child's own timetable and the parent's, so an exam reads
 * the same on both (CLAUDE.md §3). When the grid is drawn the papers sit in
 * its periods instead, as on the teacher's week; an exam week often has no
 * lesson timetable of its own, though, and the papers must still be shown.
 *
 * Draws nothing in a week with no papers.
 *
 * An exam week can carry a dozen papers, which pushed the timetable itself off
 * the screen. One row shows; the rest are a click away.
 */
const VISIBLE_PAPERS = 3;

export function WeekExams({ days }: WeekExamsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const papers = (days ?? []).flatMap((day) => day.exams ?? []);
  if (papers.length === 0) return null;

  const hidden = papers.length - VISIBLE_PAPERS;
  const shown = isExpanded ? papers : papers.slice(0, VISIBLE_PAPERS);

  return (
    <section
      aria-label="Exams this week"
      className="flex flex-col gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4"
    >
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 shrink-0 text-amber" aria-hidden />
        <h3 className="text-base font-bold text-ink">
          {papers.length === 1 ? '1 exam this week' : `${papers.length} exams this week`}
        </h3>
      </div>

      <ul id="week-exams-list" className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((paper) => (
          <WeekExamItem key={paper.uuid} paper={paper} />
        ))}
      </ul>

      {hidden > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          aria-expanded={isExpanded}
          aria-controls="week-exams-list"
          rightIcon={
            isExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            )
          }
          onClick={() => setIsExpanded((open) => !open)}
        >
          {isExpanded ? 'Show fewer' : `Show ${hidden} more ${hidden === 1 ? 'exam' : 'exams'}`}
        </Button>
      )}
    </section>
  );
}

function WeekExamItem({ paper }: { paper: FamilyExamPaper }) {
  return (
    <li className="flex flex-col gap-0.5 rounded-xl border border-line bg-surface p-3">
      <span className="text-sm font-bold text-ink">{paper.subject.name}</span>
      <span className="text-xs font-medium text-ink-2">
        {paper.name} · {EXAM_TYPE_LABEL[paper.examType]}
      </span>
      <span className="text-xs font-semibold text-ink-3 tabular-nums">
        {formatDate(paper.examDate)} · {formatTimeRange(paper.startTime, paper.endTime, 'Time not set')}
      </span>
    </li>
  );
}

/** Each column's papers, earliest first as the server sent them, for placing them in the grid. */
export function examsByDay(
  days: StudentTimetableDay[] | undefined,
): Map<StudentTimetableDay['dayOfWeek'], FamilyExamPaper[]> {
  return new Map(
    (days ?? [])
      .filter((day) => (day.exams ?? []).length > 0)
      .map((day) => [day.dayOfWeek, day.exams ?? []]),
  );
}

/**
 * The mark under a column's date on a day with papers, as the teacher's week
 * draws it. A paper with no time has no period to sit in, so the day itself
 * carries it — named, not just counted.
 */
export function ExamDayMark({ papers }: { papers: FamilyExamPaper[] }) {
  if (papers.length === 0) return null;
  const untimed = untimedExams(papers);

  return (
    <>
      <span className="mt-1 inline-block rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber">
        {papers.length === 1 ? 'Exam day' : `${papers.length} exams`}
      </span>
      {untimed.length > 0 && (
        <span className="block text-[10px] font-medium text-ink-3">
          {untimed.map((paper) => paper.subject.name).join(', ')} · time not set
        </span>
      )}
    </>
  );
}
